import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type RecurrenceBulkEditPreview,
  type RecurrenceRule,
  type TemplateEdit,
  usePreviewBulkRecurrenceEdit,
  useSplitSeries,
} from '@/entities/recurrence';
import { FormError } from '@/shared/forms';
import { formatCalendarDate } from '@/shared/lib/datetime';
import { Button, Dialog, LoadingState, OverdueToggle, TextField, TimeField } from '@/shared/ui';

import styles from './Recurrence.module.css';

interface Props {
  rule: RecurrenceRule;
  originalDate: string;
  onClose: () => void;
  onDone: () => void;
}

const clock = (value: string | null): string => value?.slice(0, 5) ?? '';

const validClock = (value: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

export function FollowingGeneratedEditDialog({ rule, originalDate, onClose, onDone }: Props) {
  const { t, i18n } = useTranslation();

  const preview = usePreviewBulkRecurrenceEdit(rule.source_card_id);
  const split = useSplitSeries(rule.source_card_id);

  const [title, setTitle] = useState(rule.title ?? '');
  const [startTime, setStartTime] = useState(clock(rule.start_time));
  const [endTime, setEndTime] = useState(clock(rule.end_time));
  const [length, setLength] = useState(String(rule.length_days ?? 0));
  const [overdue, setOverdue] = useState(rule.overdue_enabled === true);

  const [approved, setApproved] = useState<{
    signature: string;
    data: RecurrenceBulkEditPreview;
  } | null>(null);

  const days = Number(length);

  const changes: TemplateEdit = {
    ...(title.trim() !== (rule.title ?? '') && {
      title: title.trim(),
    }),
    ...(rule.is_event &&
      startTime !== clock(rule.start_time) && {
        start_time: startTime,
      }),
    ...(rule.has_deadline &&
      endTime !== clock(rule.end_time) && {
        end_time: endTime,
      }),
    ...(rule.is_event &&
      days !== (rule.length_days ?? 0) && {
        length_days: days,
      }),
    ...(rule.is_event &&
      overdue !== (rule.overdue_enabled === true) && {
        overdue_enabled: overdue,
      }),
  };

  const signature = JSON.stringify({
    originalDate,
    version: rule.version,
    changes,
  });

  const valid =
    title.trim().length > 0 &&
    title.trim().length <= 500 &&
    (!rule.is_event || startTime === clock(rule.start_time) || validClock(startTime)) &&
    (!rule.has_deadline || endTime === clock(rule.end_time) || validClock(endTime)) &&
    (!rule.is_event || (Number.isInteger(days) && days >= 0 && days <= 3650)) &&
    (!rule.is_event || days > 0 || !startTime || !endTime || endTime >= startTime);

  const hasChanges = Object.keys(changes).length > 0;

  const confirmed = approved?.signature === signature ? approved.data : null;

  const busy = preview.isPending || split.isPending;

  const requestPreview = () => {
    if (!valid || !hasChanges || busy) return;

    setApproved(null);

    preview.mutate(
      {
        scope: 'following',
        original_date: originalDate,
        expected_version: rule.version,
        template: changes,
      },
      {
        onSuccess: (data) => {
          setApproved({ signature, data });
        },
      },
    );
  };

  const save = () => {
    if (!confirmed || !valid || busy) return;

    split.mutate(
      {
        originalDate,
        expectedVersion: confirmed.rule_version,
        template: changes,
      },
      {
        onSuccess: onDone,
        onError: () => {
          setApproved(null);
        },
      },
    );
  };

  return (
    <Dialog
      title={t('recurrence.followingEdit.title')}
      size="lg"
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <div className={styles.policy} data-testid="following-generated-edit">
        <p className={styles.note}>
          {t('recurrence.followingEdit.description', {
            date: formatCalendarDate(originalDate, i18n.language),
          })}
        </p>

        <TextField
          label={t('recurrence.template.title')}
          value={title}
          maxLength={500}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
        />

        {rule.is_event && (
          <TimeField
            label={t('recurrence.template.startTime')}
            value={startTime}
            onChange={setStartTime}
          />
        )}

        {rule.has_deadline && (
          <TimeField
            label={t('recurrence.template.endTime')}
            value={endTime}
            onChange={setEndTime}
          />
        )}

        {rule.is_event && (
          <>
            <TextField
              label={t('recurrence.template.length')}
              type="number"
              min={0}
              max={3650}
              value={length}
              onChange={(event) => {
                setLength(event.target.value);
              }}
            />

            <OverdueToggle value={overdue} onChange={setOverdue} />
          </>
        )}

        <Button variant="ghost" disabled={!valid || !hasChanges || busy} onClick={requestPreview}>
          {t('recurrence.bulkEdit.preview')}
        </Button>

        {preview.isPending && <LoadingState />}

        {confirmed && (
          <div data-testid="following-generated-preview">
            <p>
              {t('recurrence.bulkEdit.total', {
                count: confirmed.materialized_cards,
              })}
            </p>
            <p>
              {t('recurrence.bulkEdit.full', {
                count: confirmed.fully_editable,
              })}
            </p>
            <p>
              {t('recurrence.bulkEdit.partial', {
                count: confirmed.partially_editable,
              })}
            </p>
            <p>
              {t('recurrence.bulkEdit.protected', {
                count: confirmed.protected,
              })}
            </p>
            <p>
              {t('recurrence.bulkEdit.titleUpdates', {
                count: confirmed.title_updates,
              })}
            </p>
            <p>
              {t('recurrence.bulkEdit.deadlineUpdates', {
                count: confirmed.deadline_updates,
              })}
            </p>
            <p className={styles.warn}>{t('recurrence.followingEdit.notice')}</p>
          </div>
        )}

        <FormError error={preview.error ?? split.error} />

        <div className={styles.actions}>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {t('common.cancel')}
          </Button>

          <Button variant="primary" disabled={!confirmed || !valid || busy} onClick={save}>
            {t('recurrence.followingEdit.confirm')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
